import { useCallback, useState, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    /** Badge to show condition for conditional fields */
    conditionBadge?: string
    /** Demo animation type */
    demo?: TourDemoType
}

/**
 * Tour hook for Analytics Dashboard
 */
export const useAnalyticsTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)

    const tourSteps: TourStep[] = [
        {
            selector: '[data-tour="target-analytics-filters"]',
            title: 'Bộ lọc dữ liệu',
            description:
                'Lọc dữ liệu theo phòng ban, nhân viên và khoảng thời gian',
        },
        {
            selector: '[data-tour="target-analytics-employee-table"]',
            title: 'Danh sách nhân viên đã lọc',
            description: 'Bảng này chỉ hiển thị với Admin/Manager',
            conditionBadge: 'Admin Only',
        },
        {
            selector: '[data-tour="target-analytics-stat-cards"]',
            title: 'Tổng quan hiệu suất',
            description: 'Tỉ lệ đạt doanh số và Thu nhập trung bình theo tháng',
        },
        {
            selector: '[data-tour="target-analytics-performance-chart"]',
            title: 'Biểu đồ so sánh',
            description: 'So sánh doanh thu thực tế và KPI trong kỳ',
        },
        {
            selector: '[data-tour="target-analytics-sales-performance"]',
            title: 'Chi tiết hiệu suất',
            description:
                'Xem trạng thái KPI, các chỉ số Activity (Presentation, POC, Contract)',
        },
        {
            selector: '[data-tour="target-analytics-services-pie"]',
            title: 'Đóng góp dịch vụ',
            description: 'Tỷ trọng dịch vụ được bán mới trong kỳ',
        },
        {
            selector: '[data-tour="target-analytics-employee-pie"]',
            title: 'Đóng góp nhân viên',
            description: 'Tỷ trọng doanh thu từng nhân viên trong kỳ',
        },
    ]

    const showTourUI = (step: TourStep, stepIndex: number) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found (e.g. Admin Only section for normal user)
            if (stepIndex < tourSteps.length - 1) {
                // Determine next step index
                const nextStepIndex = stepIndex + 1
                // We need to recurse, but use setTimeout to avoid stack overflow potentially or UI locking
                setTimeout(() => goToStep(nextStepIndex), 100)
            } else {
                endTour()
            }
            return
        }

        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Build description with condition badge if present
        let fullDescription = step.description
        if (step.conditionBadge) {
            fullDescription = `<span style="display:inline-block;background:#f3e8ff;color:#7c3aed;padding:2px 8px;border-radius:4px;font-size:11px;margin-bottom:8px;">${step.conditionBadge}</span><br/>${step.description}`
        }

        // Wait for scroll then add popover
        setTimeout(() => {
            const popover = createTourPopover({
                title: step.title,
                description: fullDescription,
                currentStep: stepIndex + 1,
                totalSteps: tourSteps.length,
                onPrev:
                    stepIndex > 0 ? () => goToStep(stepIndex - 1) : undefined,
                onNext: () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        goToStep(stepIndex + 1)
                    }
                },
                onClose: endTour,
                isLastStep: stepIndex === tourSteps.length - 1,
                demo: step.demo,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex)
        setCurrentTourStep(stepIndex)
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
    }, [])

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Wait a bit then start
        setTimeout(() => {
            showTourUI(tourSteps[0], 0)
        }, 600)
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive, currentStep: currentTourStep }
}

export default useAnalyticsTour
